import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

const MAX_RECENT_KBS = 10;
const DEBOUNCE_MS = 500;

export const useConnectedKBs = (conversationId: string | null | undefined, maxConnectedKBs: number = 3) => {
  const [connectedKBs, setConnectedKBs] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const currentConversationIdRef = useRef<string | null | undefined>(conversationId);

  // Keep ref in sync with conversationId
  useEffect(() => {
    currentConversationIdRef.current = conversationId;
  }, [conversationId]);

  // Load connected KBs from database when conversationId changes
  useEffect(() => {
    if (!conversationId) {
      setConnectedKBs([]);
      setIsLoading(false);
      return;
    }

    const loadConnectedKBs = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('giblet_conversations')
          .select('metadata')
          .eq('id', conversationId)
          .single();

        if (error) {
          console.error('Error loading connected KBs:', error);
          setConnectedKBs([]);
          return;
        }

        const metadata = data?.metadata as Record<string, any> | null;
        const kbIds = metadata?.connected_kb_ids;
        
        if (Array.isArray(kbIds)) {
          setConnectedKBs(kbIds);
        } else {
          setConnectedKBs([]);
        }
      } catch (error) {
        console.error('Failed to load connected KBs:', error);
        setConnectedKBs([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadConnectedKBs();
  }, [conversationId]);

  // Persist to database with debounce
  const persistToDatabase = useCallback(async (kbIds: string[]) => {
    const convId = currentConversationIdRef.current;
    if (!convId) return;

    try {
      // Fetch current metadata first to preserve other fields
      const { data: current, error: fetchError } = await supabase
        .from('giblet_conversations')
        .select('metadata')
        .eq('id', convId)
        .single();

      if (fetchError) {
        console.error('Error fetching conversation metadata:', fetchError);
        return;
      }

      const existingMetadata = (current?.metadata as Record<string, any>) || {};
      const updatedMetadata = {
        ...existingMetadata,
        connected_kb_ids: kbIds,
      };

      const { error: updateError } = await supabase
        .from('giblet_conversations')
        .update({ metadata: updatedMetadata })
        .eq('id', convId);

      if (updateError) {
        console.error('Error persisting connected KBs:', updateError);
      }
    } catch (error) {
      console.error('Failed to persist connected KBs:', error);
    }
  }, []);

  // Schedule debounced database update
  const schedulePersist = useCallback((kbIds: string[]) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      persistToDatabase(kbIds);
    }, DEBOUNCE_MS);
  }, [persistToDatabase]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const connectKB = useCallback((kbId: string) => {
    setConnectedKBs(prev => {
      if (prev.includes(kbId)) return prev;
      if (prev.length >= maxConnectedKBs) {
        toast.error(`Maximum ${maxConnectedKBs} KBs can be connected`);
        return prev;
      }
      
      const newKBs = [...prev, kbId];
      
      // Update recently used (localStorage)
      updateRecentlyUsed(kbId);
      
      // Schedule database persistence
      schedulePersist(newKBs);
      
      return newKBs;
    });
  }, [maxConnectedKBs, schedulePersist]);

  const disconnectKB = useCallback((kbId: string) => {
    setConnectedKBs(prev => {
      const newKBs = prev.filter(id => id !== kbId);
      
      // Schedule database persistence
      schedulePersist(newKBs);
      
      return newKBs;
    });
  }, [schedulePersist]);

  // Bulk set for loading threads - does NOT persist (caller handles that)
  const setConnectedKBsDirectly = useCallback((kbIds: string[]) => {
    setConnectedKBs(kbIds);
  }, []);

  const updateRecentlyUsed = (kbId: string) => {
    try {
      const recent = JSON.parse(localStorage.getItem('giblet_recent_kbs') || '[]');
      const updated = [kbId, ...recent.filter((id: string) => id !== kbId)].slice(0, MAX_RECENT_KBS);
      localStorage.setItem('giblet_recent_kbs', JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to update recently used KBs:', error);
    }
  };

  const getRecentlyUsed = (): string[] => {
    try {
      return JSON.parse(localStorage.getItem('giblet_recent_kbs') || '[]');
    } catch (error) {
      console.error('Failed to get recently used KBs:', error);
      return [];
    }
  };

  return {
    connectedKBs,
    connectKB,
    disconnectKB,
    setConnectedKBsDirectly,
    isLoading,
    canConnectMore: connectedKBs.length < maxConnectedKBs,
    maxConnectedKBs,
    getRecentlyUsed
  };
};
